import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Upload, Download, Loader2, Pencil, Trash2, Search, X, Mail, MessageCircle, Ban } from 'lucide-react';
import { toast } from 'react-toastify';
import { nl, errorMsg } from './newsletter.api';

const VACIO = { name: '', email: '', phone: '', segment: 'actual', tags: '', notes: '' };
const input = 'block w-full border border-gray-300 rounded-lg px-3 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500';

export const SEGMENTO = {
  actual: { txt: 'Inversor actual', cls: 'text-emerald-700 bg-emerald-50' },
  potencial: { txt: 'Inversor potencial', cls: 'text-violet-700 bg-violet-50' },
};

/** CSV o lo que se pega desde Excel (coma, punto y coma o tab). Reconoce los encabezados en español. */
export function leerPlanilla(texto) {
  const lineas = String(texto || '').replace(/^﻿/, '').split(/\r?\n/).filter((l) => l.trim());
  if (!lineas.length) return [];
  const sep = [';', '\t', ','].find((s) => lineas[0].includes(s)) || ',';
  const partir = (l) => {
    const out = []; let cur = ''; let q = false;
    for (const ch of l) {
      if (ch === '"') q = !q;
      else if (ch === sep && !q) { out.push(cur.trim()); cur = ''; }
      else cur += ch;
    }
    out.push(cur.trim());
    return out;
  };
  const cab = partir(lineas[0]).map((h) => h.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, ''));
  const col = (...nombres) => cab.findIndex((h) => nombres.some((n) => h.includes(n)));
  const idx = { name: col('nombre', 'name', 'apellido'), email: col('mail', 'correo'), phone: col('tel', 'celu', 'whats', 'phone', 'movil'), segment: col('tipo', 'segmento', 'inversor'), tags: col('etiqueta', 'tag', 'unidad', 'grupo') };
  const tieneCab = Object.values(idx).some((i) => i >= 0);
  const filas = (tieneCab ? lineas.slice(1) : lineas).map(partir);
  const pos = tieneCab ? idx : { name: 0, email: 1, phone: 2, segment: 3, tags: 4 };
  return filas.map((f) => ({
    name: pos.name >= 0 ? f[pos.name] : '',
    email: pos.email >= 0 ? f[pos.email] : '',
    phone: pos.phone >= 0 ? f[pos.phone] : '',
    segment: pos.segment >= 0 && /pot/i.test(f[pos.segment] || '') ? 'potencial' : 'actual',
    tags: pos.tags >= 0 ? f[pos.tags] : '',
  })).filter((r) => r.name || r.email || r.phone);
}

function Formulario({ inicial, onGuardar, onCerrar }) {
  const [f, setF] = useState(inicial);
  const [guardando, setGuardando] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const guardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try { await onGuardar({ ...f, tags: String(f.tags || '').split(',').map((t) => t.trim()).filter(Boolean) }); } catch (err) { toast.error(errorMsg(err)); }
    setGuardando(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-6" onClick={onCerrar}>
      <form onSubmit={guardar} className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="font-semibold text-gray-900">{inicial.id ? 'Editar contacto' : 'Nuevo contacto'}</div>
          <button type="button" onClick={onCerrar} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500" aria-label="Cerrar"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(SEGMENTO).map(([k, s]) => (
            <button key={k} type="button" onClick={() => set('segment', k)}
              className={`rounded-lg border px-3 py-2.5 text-sm font-semibold ${f.segment === k ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-300 text-gray-600'}`}>{s.txt}</button>
          ))}
        </div>
        <label className="block space-y-1.5"><span className="text-sm font-semibold text-gray-800">Nombre y apellido</span><input className={input} style={{ height: 42 }} value={f.name} onChange={(e) => set('name', e.target.value)} required /></label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block space-y-1.5"><span className="text-sm font-semibold text-gray-800">Mail</span><input type="email" className={input} style={{ height: 42 }} value={f.email || ''} onChange={(e) => set('email', e.target.value)} /></label>
          <label className="block space-y-1.5"><span className="text-sm font-semibold text-gray-800">WhatsApp</span><input className={input} style={{ height: 42 }} value={f.phone || ''} onChange={(e) => set('phone', e.target.value)} placeholder="11 2345-6789" /></label>
        </div>
        <label className="block space-y-1.5"><span className="text-sm font-semibold text-gray-800">Etiquetas <span className="font-normal text-gray-500">(opcional, separadas por coma)</span></span><input className={input} style={{ height: 42 }} value={f.tags} onChange={(e) => set('tags', e.target.value)} placeholder="Torre A, 3° piso" /></label>
        {inicial.id && (inicial.unsubscribedEmail || inicial.unsubscribedWhatsapp) && <div className="text-xs text-amber-700 bg-amber-50 rounded-lg p-2.5">Este contacto se dio de baja: no le llega el newsletter.</div>}
        <button type="submit" disabled={guardando} className="w-full inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60">
          <Loader2 size={16} className={guardando ? 'animate-spin' : 'hidden'} /><span>Guardar</span>
        </button>
      </form>
    </div>
  );
}

/** Lee la primera hoja de un Excel o CSV y devuelve filas (la primera, los encabezados). */
async function leerArchivo(file) {
  const XLSX = await import('xlsx');
  // Los CSV se leen como texto UTF-8 (si no, los acentos salen mal); los Excel, como binario.
  const wb = /\.(csv|txt)$/i.test(file.name) ? XLSX.read(await file.text(), { type: 'string' }) : XLSX.read(await file.arrayBuffer(), { type: 'array' });
  const hoja = wb.Sheets[wb.SheetNames[0]];
  return XLSX.utils.sheet_to_json(hoja, { header: 1, raw: false, defval: '' }).filter((r) => r.some((c) => String(c).trim()));
}
const sinAcentos = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
const CAMPOS = [
  ['name', 'Nombre', ['nombre', 'name', 'apellido', 'cliente', 'contacto']],
  ['email', 'Mail', ['mail', 'correo', 'email']],
  ['phone', 'Teléfono / WhatsApp', ['tel', 'celu', 'whats', 'phone', 'movil', 'cel']],
  ['segment', 'Tipo (actual / potencial)', ['tipo', 'segmento', 'inversor', 'estado']],
  ['tags', 'Etiquetas', ['etiqueta', 'tag', 'unidad', 'grupo', 'torre', 'depto']],
];
const adivinar = (cab) => Object.fromEntries(CAMPOS.map(([k, , pistas]) => [k, cab.findIndex((h) => pistas.some((p) => sinAcentos(h).includes(p)))]));

function Importar({ ctx, onListo, onCerrar }) {
  const [filas, setFilas] = useState(null); // [encabezados, ...datos]
  const [mapa, setMapa] = useState({});
  const [tipoDefecto, setTipoDefecto] = useState('actual');
  const [enviando, setEnviando] = useState(false);
  const [leyendo, setLeyendo] = useState(false);
  const [encima, setEncima] = useState(false);
  const archivo = useRef(null);

  const tomar = async (f) => {
    if (!f) return;
    setLeyendo(true);
    try {
      const r = await leerArchivo(f);
      if (r.length < 2) throw new Error('El archivo no tiene filas con datos');
      setFilas(r); setMapa(adivinar(r[0].map(String)));
    } catch (e) { toast.error(`No pudimos leer el archivo: ${e.message}`); }
    setLeyendo(false);
  };
  const pegar = (texto) => { const r = leerPlanilla(texto); if (!r.length) return; setFilas([['Nombre', 'Mail', 'Teléfono', 'Tipo', 'Etiquetas'], ...r.map((x) => [x.name, x.email, x.phone, x.segment, x.tags])]); setMapa({ name: 0, email: 1, phone: 2, segment: 3, tags: 4 }); };

  const contactos = useMemo(() => {
    if (!filas) return [];
    const val = (row, k) => (mapa[k] >= 0 ? String(row[mapa[k]] ?? '').trim() : '');
    return filas.slice(1).map((row) => ({
      name: val(row, 'name'), email: val(row, 'email'), phone: val(row, 'phone'),
      segment: mapa.segment >= 0 ? (/pot|interes|lead|prospect/i.test(val(row, 'segment')) ? 'potencial' : /act|invir|compr|client|propiet/i.test(val(row, 'segment')) ? 'actual' : tipoDefecto) : tipoDefecto,
      tags: val(row, 'tags'),
    })).filter((r) => r.name || r.email || r.phone);
  }, [filas, mapa, tipoDefecto]);

  const plantilla = () => {
    const csv = 'nombre;mail;telefono;tipo;etiquetas\nAna Pérez;ana@ejemplo.com;11 2345-6789;actual;Torre A\nJuan Gómez;juan@ejemplo.com;;potencial;\n';
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
    a.download = 'inversores-newsletter.csv';
    document.body.appendChild(a); a.click(); a.remove();
  };
  const importar = async () => {
    setEnviando(true);
    try {
      const r = await nl.importContacts(ctx.customerId, contactos);
      toast.success(`${r.added} inversores cargados${r.duplicated ? ` · ${r.duplicated} ya estaban` : ''}`);
      if (r.errorCount) toast.warn(`${r.errorCount} filas con problemas: ${r.errors.slice(0, 3).join(' · ')}`);
      onListo();
    } catch (e) { toast.error(errorMsg(e)); }
    setEnviando(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-6" onClick={onCerrar}>
      <div className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl p-5 space-y-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold text-gray-900">Importar inversores</div>
            <div className="text-sm text-gray-500">Subí tu Excel o CSV tal como lo tenés: después elegís qué columna es cada dato.</div>
          </div>
          <button type="button" onClick={onCerrar} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500" aria-label="Cerrar"><X size={18} /></button>
        </div>

        {!filas ? (
          <>
            <div onDragOver={(e) => { e.preventDefault(); setEncima(true); }} onDragLeave={() => setEncima(false)} onDrop={(e) => { e.preventDefault(); setEncima(false); tomar(e.dataTransfer.files?.[0]); }}
              onClick={() => archivo.current?.click()}
              className={`cursor-pointer rounded-2xl border-2 border-dashed p-8 text-center transition ${encima ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400'}`}>
              {leyendo ? <Loader2 className="mx-auto animate-spin text-blue-600" /> : <Upload className="mx-auto text-blue-600" />}
              <div className="font-semibold text-gray-900 mt-2">Arrastrá tu Excel o CSV acá</div>
              <div className="text-xs text-gray-500 mt-1">o tocá para elegirlo · .xlsx, .xls o .csv</div>
              <input ref={archivo} type="file" accept=".xlsx,.xls,.csv,.txt" className="hidden" onChange={(e) => { tomar(e.target.files?.[0]); e.target.value = ''; }} />
            </div>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <button type="button" onClick={plantilla} className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 rounded-lg px-2 py-1 hover:bg-blue-50"><Download size={14} /><span>Bajar planilla de ejemplo</span></button>
              <span className="text-xs text-gray-500">¿Lo tenés copiado? Pegalo abajo.</span>
            </div>
            <textarea onChange={(e) => pegar(e.target.value)} placeholder={'nombre;mail;telefono;tipo\nAna Pérez;ana@ejemplo.com;11 2345-6789;actual'} className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500" style={{ height: 90 }} />
          </>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {CAMPOS.map(([k, label]) => (
                <label key={k} className="block space-y-1">
                  <span className="text-xs font-semibold text-gray-700">{label}</span>
                  <select value={mapa[k] ?? -1} onChange={(e) => setMapa((m) => ({ ...m, [k]: Number(e.target.value) }))} className="block w-full border border-gray-300 rounded-lg px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" style={{ height: 38 }}>
                    <option value={-1}>— No usar —</option>
                    {filas[0].map((h, i) => <option key={i} value={i}>{String(h) || `Columna ${i + 1}`}</option>)}
                  </select>
                </label>
              ))}
              {!(mapa.segment >= 0) && (
                <label className="block space-y-1"><span className="text-xs font-semibold text-gray-700">Todos son</span>
                  <select value={tipoDefecto} onChange={(e) => setTipoDefecto(e.target.value)} className="block w-full border border-gray-300 rounded-lg px-3 text-sm bg-white" style={{ height: 38 }}><option value="actual">Inversores actuales</option><option value="potencial">Inversores potenciales</option></select>
                </label>
              )}
            </div>
            <div className="border border-gray-200 rounded-lg overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-500"><tr><th className="text-left p-2">Nombre</th><th className="text-left p-2">Mail</th><th className="text-left p-2">Teléfono</th><th className="text-left p-2">Tipo</th></tr></thead>
                <tbody>{contactos.slice(0, 5).map((r, i) => <tr key={i} className="border-t border-gray-100"><td className="p-2">{r.name}</td><td className="p-2">{r.email}</td><td className="p-2">{r.phone}</td><td className="p-2">{SEGMENTO[r.segment].txt}</td></tr>)}</tbody>
              </table>
              {contactos.length > 5 && <div className="text-xs text-gray-500 p-2 border-t border-gray-100">y {contactos.length - 5} más…</div>}
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setFilas(null)} className="rounded-lg px-4 py-2.5 text-sm font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50">Otro archivo</button>
              <button type="button" disabled={!contactos.length || enviando} onClick={importar} className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
                <Loader2 size={16} className={enviando ? 'animate-spin' : 'hidden'} /><span>Importar {contactos.length} inversores</span>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function Contactos({ ctx }) {
  const [lista, setLista] = useState(null);
  const [filtro, setFiltro] = useState('todos');
  const [buscar, setBuscar] = useState('');
  const [form, setForm] = useState(null);
  const [importar, setImportar] = useState(false);

  const cargar = useCallback(() => nl.contacts(ctx.customerId).then(setLista).catch((e) => { toast.error(errorMsg(e)); setLista([]); }), [ctx.customerId]);
  useEffect(() => { cargar(); }, [cargar]);

  const visibles = useMemo(() => {
    const q = buscar.trim().toLowerCase();
    return (lista || []).filter((c) => (filtro === 'todos' || c.segment === filtro) && (!q || [c.name, c.email, c.phone, ...(c.tags || [])].join(' ').toLowerCase().includes(q)));
  }, [lista, filtro, buscar]);

  const cuenta = (seg) => (lista || []).filter((c) => seg === 'todos' || c.segment === seg).length;

  const guardar = async (c) => {
    if (c.id) await nl.updateContact(c.id, c);
    else await nl.addContact(ctx.customerId, c);
    toast.success('Contacto guardado');
    setForm(null);
    cargar();
  };

  if (!lista) return <div className="bg-white border border-gray-200 rounded-2xl p-6 flex items-center gap-3 text-gray-600"><Loader2 size={18} className="animate-spin" />Cargando contactos…</div>;

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-5 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-1.5 flex-wrap">
          {[['todos', 'Todos'], ['actual', 'Actuales'], ['potencial', 'Potenciales']].map(([k, t]) => (
            <button key={k} type="button" onClick={() => setFiltro(k)} className={`text-sm font-semibold px-3 py-1.5 rounded-full ${filtro === k ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{t} <span className="opacity-60">{cuenta(k)}</span></button>
          ))}
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => setImportar(true)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-800 border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50"><Upload size={14} /><span>Importar Excel / CSV</span></button>
          <button type="button" onClick={() => setForm(VACIO)} className="inline-flex items-center gap-1.5 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-2"><Plus size={14} /><span>Agregar</span></button>
        </div>
      </div>
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input value={buscar} onChange={(e) => setBuscar(e.target.value)} placeholder="Buscar por nombre, mail, teléfono o etiqueta" className={`${input} pl-9`} style={{ height: 40 }} />
      </div>

      {!lista.length ? (
        <div className="text-center py-10">
          <div className="font-semibold text-gray-900">Cargá a tus inversores</div>
          <div className="text-sm text-gray-500 mt-1">De a uno o importando la planilla que ya tenés.</div>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {visibles.map((c) => (
            <div key={c.id} className="py-3 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-sm font-semibold shrink-0">{(c.name || '?').slice(0, 1).toUpperCase()}</div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-gray-900 text-sm truncate">{c.name}</span>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${SEGMENTO[c.segment]?.cls}`}>{SEGMENTO[c.segment]?.txt}</span>
                  {(c.unsubscribedEmail || c.unsubscribedWhatsapp) && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full"><Ban size={11} />Dado de baja</span>}
                  {(c.tags || []).map((t) => <span key={t} className="text-[11px] text-gray-600 bg-gray-50 border border-gray-200 px-2 py-0.5 rounded-full">{t}</span>)}
                </div>
                <div className="flex flex-wrap gap-x-4 text-xs text-gray-500 mt-0.5">
                  {c.email && <span className="inline-flex items-center gap-1"><Mail size={12} />{c.email}</span>}
                  {c.phone && <span className="inline-flex items-center gap-1"><MessageCircle size={12} />+{c.phone}</span>}
                </div>
              </div>
              <button type="button" onClick={() => setForm({ ...c, tags: (c.tags || []).join(', '), email: c.email || '', phone: c.phone || '' })} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500" aria-label="Editar"><Pencil size={15} /></button>
              <button type="button" onClick={async () => { if (!window.confirm(`¿Borrar a ${c.name}?`)) return; try { await nl.deleteContact(c.id); cargar(); } catch (e) { toast.error(errorMsg(e)); } }} className="p-2 rounded-lg hover:bg-red-50 text-red-500" aria-label="Borrar"><Trash2 size={15} /></button>
            </div>
          ))}
          {!visibles.length && <div className="text-sm text-gray-500 py-6 text-center">No hay contactos con ese filtro.</div>}
        </div>
      )}

      {form && <Formulario inicial={form} onGuardar={guardar} onCerrar={() => setForm(null)} />}
      {importar && <Importar ctx={ctx} onListo={() => { setImportar(false); cargar(); }} onCerrar={() => setImportar(false)} />}
    </div>
  );
}
